import React, { useContext, useEffect, useRef, useState } from 'react';
import { site_url } from "./../config.js";
import { getOfflineOrders } from "../services/offlineOrderService";
import { ConnectivityContext } from "../context/ConnectivityContext";
import { cachedGet } from '../services/apiCacheService';

export default function RecentBills() {
    const [orders, setOrders] = useState([]);
    const [offlineOrders, setOfflineOrders] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const sentinelRef = useRef(null);
    const PAGE_SIZE = 20;
    const { runSync, syncing, networkOnline } = useContext(ConnectivityContext);

    const fetchOrders = async (reset = false, nextPage = page) => {
        try {
            setIsLoading(true);
            const params = {
                page: nextPage,
                per_page: PAGE_SIZE,
            };
            if (fromDate) params.fromDate = fromDate;
            if (toDate) params.toDate = toDate;
            const responseData = await cachedGet(site_url + '/orders/recent', { params }, !networkOnline);
            const items = responseData?.orders || [];
            setOrders(reset ? items : [...orders, ...items]);
            setHasMore(items.length === PAGE_SIZE);
        } catch (e) {
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // initial load or when filters change
        setPage(1);
        setHasMore(true);
        fetchOrders(true, 1);
        loadOfflineOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);

    const loadOfflineOrders = async () => {
        const rows = await getOfflineOrders();
        setOfflineOrders(rows);
    };

    useEffect(() => {
        if (!sentinelRef.current) return;
        const el = sentinelRef.current;
        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting && hasMore && !isLoading) {
                const next = page + 1;
                setPage(next);
                fetchOrders(false, next);
            }
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sentinelRef.current, page, hasMore, isLoading]);

    const onFilter = (e) => {
        e.preventDefault();
        setPage(1);
        fetchOrders(true);
    };

    return (
        <div className="container mt-3">
            <h3>Recent Bills</h3>
            <div className="mb-2">
                <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={async () => {
                        await runSync();
                        await loadOfflineOrders();
                        await fetchOrders(true, 1);
                    }}
                    disabled={syncing || !networkOnline}
                >
                    {syncing ? "Syncing..." : "Sync Now"}
                </button>
            </div>
            <form className="row g-2" onSubmit={onFilter}>
                <div className="col-auto">
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="form-control" />
                </div>
                <div className="col-auto">
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="form-control" />
                </div>
                <div className="col-auto">
                    <button className="btn btn-primary" type="submit">Filter</button>
                </div>
            </form>

            <div className="mt-3">
                {offlineOrders.map((o) => (
                    <div className="card mb-2 border-warning" key={`offline-${o.id}`}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <b>Offline #{o.offline_id}</b>
                                <span>{o.created_at}</span>
                            </div>
                            <div>Shop: {o.order_data?.shopid}</div>
                            <div>Worker: {o.order_data?.userid}</div>
                            <div>Sync status: <b>{o.sync_status}</b></div>
                        </div>
                    </div>
                ))}
                {orders.map((o) => (
                    <div className="card mb-2" key={o.bill_id}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <b>#{o.bill_id}</b>
                                <span>{o.bill_date || o.created_at}</span>
                            </div>
                            <div>Shop: {o.vendor_name} ({o.shop_id})</div>
                            <div>Route: {o.route_ename || o.route_name || '-'}</div>
                            <div>Worker: {o.worker_name || '-'}</div>
                            <div>Price Type: {o.price_type === 1 ? 'Owl' : 'Normal'}</div>
                            <div>Final Amount: {o.final_amount}</div>
                            <div>Sync status: <b>{o.offline_id ? 'synced' : 'synced'}</b></div>
                        </div>
                    </div>
                ))}
                {/* Sentinel for infinite scroll */}
                <div ref={sentinelRef} style={{ height: 1 }} />
            </div>
            {isLoading && <div className="text-center p-2"><small>Loading...</small></div>}
        </div>
    );
}


